import { modelToApiMapping } from "@/api";
import { v4 as uuidv4 } from "uuid";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import db from "@/db";
import { RESYNCED_TABLES, recordKey, referencesAny } from "@/db/pendingRecord";
import {
  failTracked,
  isProjectPending,
  markRecordsPending,
  markSkeleton,
  rejectTracked,
  restoreTracked,
  trackedRows,
  unmarkRecords,
} from "@/db/pendingSync";
import {
  isNetworkError,
  isTransientFailure,
  logRequestFailure,
} from "@/utils/network";
import { reportDeviceDataError } from "@/utils/deviceDataError";
import { SAVE_REQUEST, SYNC_REQUEST } from "@/api/utils/requestTimeouts";
import { OfflineError } from "@/utils/errors";
import { useNetworkStore } from "@/composables/network/useNetwork";
import { SYNC_STEPS, markStep } from "@/composables/network/syncProgress";

// local copy: importing offlineMode here creates an import cycle through the projects store
const isOfflineMode = () => useNetworkStore().isOfflineMode;

const announceKept = async () => {
  const { notifyKeptOnDevice } =
    await import("@/composables/network/offlineMode");
  notifyKeptOnDevice();
  const { pushPendingSoon } =
    await import("@/composables/network/usePendingSync");
  pushPendingSoon();
};

const clearFlags = (data) =>
  Array.isArray(data)
    ? data.map(clearFlags)
    : { isCreatedOffline: false, isUpdatedOffline: false, ...data };

const innerFuncs = (model, client = null) => {
  if (!client) client = modelToApiMapping[model.tableName];
  const dexieTable = db[model.tableName];

  const isResynced = RESYNCED_TABLES.has(model.tableName);

  const sendOnline = async (isOfflineState, uuids, send) => {
    if (isOfflineState) return { data: null, keepFor: null };
    if (!isResynced) return { data: await send(new Set()), keepFor: null };
    const { useProjectsStore } = await import("@/store/modules/projects");
    const projectUuid = useProjectsStore().currentProjectUuid;
    if (!projectUuid) return { data: await send(new Set()), keepFor: null };
    const kept = { data: null, keepFor: projectUuid };
    if (isOfflineMode()) return kept;
    const rows = await trackedRows(model.tableName, uuids);
    if (
      (await isProjectPending(projectUuid)) ||
      rows.some((row) => !row.rejected)
    )
      return kept;
    try {
      const data = await send(new Set(rows.map(({ uuid }) => uuid)));
      if (!rows.length) return { data, keepFor: null };
      await unmarkRecords(rows.map(({ key }) => key));
      return { data: clearFlags(data), keepFor: null };
    } catch (error) {
      if (!isNetworkError(error)) throw error;
      return kept;
    }
  };

  const sendNow = async (isOfflineState, uuids, send) => {
    if (isOfflineState) throw new OfflineError();
    return { data: await send(new Set()), keepFor: null };
  };

  const keepOnDevice = async (keepFor, uuids, write) => {
    if (!keepFor) return write();
    const previous = await trackedRows(model.tableName, uuids);
    let saved;
    try {
      const { default: router } = await import("@/router");
      await markRecordsPending(
        keepFor,
        model.tableName,
        uuids,
        router.currentRoute.value.fullPath,
        isOfflineMode()
      );
      saved = await write();
    } catch (error) {
      await restoreTracked(model.tableName, uuids, previous).catch(() => {});
      reportDeviceDataError(error, {
        risk: "keep_failed",
        table: model.tableName,
        keys: uuids.map((uuid) => recordKey(model.tableName, uuid)),
      });
      throw error;
    }
    announceKept().catch((error) =>
      console.error("Kept change not announced", error)
    );
    return saved;
  };

  const withClientUuid = (item) =>
    isResynced ? { ...item, uuid: item.uuid || uuidv4() } : item;

  const keptCreate = (item) => ({
    uuid: item.uuid || uuidv4(),
    createdAt: new Date(),
    updatedAt: new Date(),
    isCreatedOffline: true,
    ...item,
  });

  const keptBulkCreate = async (items) => {
    const known = new Set(
      await dexieTable
        .where("uuid")
        .anyOf(items.map(({ uuid }) => uuid).filter(Boolean))
        .primaryKeys()
    );
    return cloneDeep(
      items.map((item) =>
        known.has(item.uuid)
          ? { isUpdatedOffline: true, ...item }
          : keptCreate(item)
      )
    );
  };

  const destroyOnBack = (uuid) =>
    client.destroy(uuid, SAVE_REQUEST).catch((error) => {
      if (error?.response?.status !== 404) throw error;
    });

  const upsertAll = (rows) =>
    Promise.all(rows.map((row) => model.createOrUpdate(row)));

  const loadBuilt = async (uuids) => {
    const objects = await dexieTable.where("uuid").anyOf(uuids).toArray();
    for (const object of objects) {
      await object.setRelationships();
    }
    return objects;
  };

  const loadOne = async (uuid) => {
    const object = await dexieTable.get({ uuid });
    await object.setRelationships();
    return object;
  };

  const innerCreate = async (
    form,
    isOfflineState,
    { requiresNetwork = false } = {}
  ) => {
    form = withClientUuid(model.toPayload(form));
    const { data, keepFor } = await (requiresNetwork ? sendNow : sendOnline)(
      isOfflineState,
      [form.uuid].filter(Boolean),
      () => client.create(form, SAVE_REQUEST)
    );
    const row = data ?? cloneDeep(keptCreate(form));
    return loadOne(
      await keepOnDevice(keepFor, [row.uuid], () => model.createOrUpdate(row))
    );
  };

  const innerBulkCreate = async (
    form,
    isOfflineState,
    { requiresNetwork = false } = {}
  ) => {
    form = form.map((item) => withClientUuid(model.toPayload(item)));
    const { data, keepFor } = await (requiresNetwork ? sendNow : sendOnline)(
      isOfflineState,
      form.map(({ uuid }) => uuid).filter(Boolean),
      () => client.bulkCreate(form, SAVE_REQUEST)
    );
    const rows = data ?? (await keptBulkCreate(form));
    return loadBuilt(
      await keepOnDevice(
        keepFor,
        rows.map(({ uuid }) => uuid),
        () => upsertAll(rows)
      )
    );
  };

  const sendWhole = async (item) => {
    const local = await dexieTable.get({ uuid: item.uuid });
    if (!local) return client.update(item, undefined, SAVE_REQUEST);
    const whole = { ...(await local.asForm()), ...item };
    return local.isCreatedOffline
      ? createOnBack(whole, SAVE_REQUEST)
      : client.update(whole, undefined, SAVE_REQUEST);
  };

  const bulkUpdateWith = async (items, rejected) => {
    const others = items.filter(({ uuid }) => !rejected.has(uuid));
    const [updated, resent] = await Promise.all([
      others.length ? client.bulkUpdate(others, SAVE_REQUEST) : [],
      Promise.all(
        items.filter(({ uuid }) => rejected.has(uuid)).map(sendWhole)
      ),
    ]);
    return [...updated, ...resent];
  };

  const updateOne = async (
    form,
    isOfflineState,
    send,
    { requiresNetwork = false } = {}
  ) => {
    form = model.toPayload(form);
    const { data, keepFor } = await (requiresNetwork ? sendNow : sendOnline)(
      isOfflineState,
      [form.uuid],
      (rejected) => (rejected.size ? sendWhole(form) : send(form))
    );
    const row = data ?? cloneDeep({ isUpdatedOffline: true, ...form });
    return loadOne(
      await keepOnDevice(keepFor, [form.uuid], () => model.createOrUpdate(row))
    );
  };

  const innerUpdate = (form, isOfflineState, options) =>
    updateOne(
      form,
      isOfflineState,
      (payload) => client.update(payload, undefined, SAVE_REQUEST),
      options
    );

  const innerPartialUpdate = (form, isOfflineState) =>
    updateOne(form, isOfflineState, (payload) =>
      client.partialUpdate(payload, undefined, SAVE_REQUEST)
    );

  const innerBulkUpdate = async (form, isOfflineState) => {
    form = form.map((item) => model.toPayload(item));
    const { data, keepFor } = await sendOnline(
      isOfflineState,
      form.map(({ uuid }) => uuid),
      (rejected) =>
        rejected.size
          ? bulkUpdateWith(form, rejected)
          : client.bulkUpdate(form, SAVE_REQUEST)
    );
    const rows =
      data ??
      cloneDeep(form.map((item) => ({ isUpdatedOffline: true, ...item })));
    return loadBuilt(
      await keepOnDevice(
        keepFor,
        form.map(({ uuid }) => uuid),
        () => upsertAll(rows)
      )
    );
  };
  const innerFetch = async (filters, isOfflineState, setIsLoading = null) => {
    if (isOfflineState || !window.navigator.onLine || isOfflineMode()) return;

    let results;
    if (setIsLoading) setIsLoading(true);
    try {
      try {
        results = await client.listAll(filters, SYNC_REQUEST);
        if (results.results) {
          results = results.results;
        }
      } catch (err) {
        logRequestFailure("Error while fetching data", err);
        return { success: false, error: err };
      }

      try {
        await db.transaction("rw", dexieTable, db.pendingSyncs, async () => {
          for (const object of results) {
            model.createOrUpdate(object);
          }
        });
        return { success: true, error: null, results: results };
      } catch (err) {
        console.error(err);
        reportDeviceDataError(err, {
          risk: "store_failed",
          table: model.tableName,
        });
        return { success: false, error: err };
      }
    } finally {
      if (setIsLoading) setIsLoading(false);
    }
  };

  const innerDelete = async (
    uuid,
    isOfflineState,
    installationUuid,
    depth = 0
  ) => {
    const obj = await dexieTable.get({ uuid: uuid });
    if (!obj) return;
    const relateds = await obj.getRelatedToDelete();
    // INFO - B.L - 03/01/2023 - If depth > 0 it means we are in a recusive
    // we don't need to call delete on client as postgres will already cascade delete
    if (!isOfflineState && !depth && !obj.isCreatedOffline)
      await destroyOnBack(uuid);
    await dexieTable.delete(uuid);

    if (isOfflineState && !obj.isCreatedOffline) {
      db.garbages.add({
        uuid,
        installationUuid,
        table: model.tableName,
        backup: obj,
      });
    }
    // INFO - B.L - 05/01/2023 - Cascade deleting all foreign key
    for (const value of Object.values(relateds)) {
      if (Array.isArray(value) && value.length) {
        const subModel = value[0].constructor;
        const subFuncs = innerFuncs(subModel);
        for (const obj of value) {
          subFuncs.innerDelete(
            obj.uuid,
            isOfflineState,
            installationUuid,
            depth + 1
          );
        }
      }
      if (Array.isArray(value) || !value) continue;
      const subModel = value.constructor;
      const subFuncs = innerFuncs(subModel);
      subFuncs.innerDelete(
        value.uuid,
        isOfflineState,
        installationUuid,
        depth + 1
      );
    }
  };

  const innerBulkDelete = async (
    form,
    isOfflineState,
    installationUuid,
    useArray
  ) => {
    let uuids;
    if (Array.isArray(form)) {
      uuids = form;
      form = useArray ? uuids : { uuids };
    } else {
      uuids = form.uuids;
    }

    if (!uuids)
      throw new Error("UUIDs are required for bulk delete operation", uuids);

    if (!isOfflineState) {
      await client.bulkDestroy(form, SAVE_REQUEST);
      await dexieTable.where("uuid").anyOf(uuids).delete();
      return;
    }

    for (const uuid of uuids) {
      await innerDelete(uuid, isOfflineState, installationUuid);
    }
  };

  const isUnchangedSince = async (uuid, sentForm) => {
    const current = await dexieTable.get({ uuid });
    if (!current) return false;
    return isEqual(await current.asForm(), sentForm);
  };

  const storeSynced = async (uuid, sentForm, data) => {
    if (await isUnchangedSince(uuid, sentForm)) {
      await dexieTable.update(
        data.uuid,
        await model.formatData({
          isStoredOffline: false,
          isUpdatedOffline: false,
          isCreatedOffline: false,
          ...data,
        })
      );
      return;
    }
    await dexieTable.update(uuid, {
      isCreatedOffline: false,
      isUpdatedOffline: true,
    });
  };

  const createOnBack = async (form, config = SYNC_REQUEST) => {
    try {
      return await client.create(form, config);
    } catch (error) {
      if (!error?.response || !form.uuid) throw error;
      const existing = await client
        .retrieve(form.uuid, config)
        .catch(() => null);
      if (!existing) throw error;
      return client.update(form, undefined, config);
    }
  };

  const resyncOne = async (obj) => {
    if (obj.isCreatedOffline) {
      const form = await obj.asForm();
      await storeSynced(obj.uuid, form, await createOnBack(form));
    } else if (obj.isUpdatedOffline) {
      const form = await obj.asForm();
      await storeSynced(
        obj.uuid,
        form,
        await client.update(form, undefined, SYNC_REQUEST)
      );
    } else if (obj.isStoredOffline) {
      dexieTable.update(obj.uuid, { isStoredOffline: false });
    }
  };

  const recordResyncFailure = async (obj, error) => {
    if (await rejectTracked(model.tableName, [obj.uuid], error)) return;
    const tracked = await trackedRows(model.tableName, [obj.uuid]);
    if (tracked.length || !obj.isCreatedOffline || isNetworkError(error)) {
      await failTracked(model.tableName, [obj.uuid], error);
      if (isTransientFailure(error)) throw error;
      return;
    }
    console.error(error);
    reportDeviceDataError(error, {
      risk: "untracked_not_sent",
      table: model.tableName,
      keys: [recordKey(model.tableName, obj.uuid)],
    });
  };

  const resyncEach = async (
    objects,
    resync,
    {
      blocked = new Set(),
      toForm = (obj) => obj.asForm(),
      doneStep = SYNC_STEPS.SENT,
    } = {}
  ) => {
    const block = (obj) => {
      if (obj.isCreatedOffline) blocked.add(obj.uuid);
    };
    for (const obj of objects) {
      const key = recordKey(model.tableName, obj.uuid);
      if (blocked.size && referencesAny(await toForm(obj), blocked)) {
        markStep(key, SYNC_STEPS.BLOCKED);
        block(obj);
        continue;
      }
      try {
        markStep(key, SYNC_STEPS.SENDING);
        await resync(obj);
        markStep(key, doneStep);
      } catch (error) {
        markStep(key, SYNC_STEPS.WAITING);
        await recordResyncFailure(obj, error);
        block(obj);
      }
    }
    return objects;
  };

  const resyncToBack = (objects, { blocked } = {}) =>
    resyncEach(Array.isArray(objects) ? objects : [objects], resyncOne, {
      blocked,
    });

  const createSkeletonsOnBack = (objects, toSkeleton, { blocked } = {}) =>
    resyncEach(
      objects.filter((obj) => obj.isCreatedOffline),
      async (obj) => {
        await createOnBack(toSkeleton(await obj.asForm()));
        await dexieTable.update(obj.uuid, {
          isCreatedOffline: false,
          isUpdatedOffline: true,
        });
        await markSkeleton(model.tableName, obj.uuid);
      },
      {
        blocked,
        toForm: async (obj) => toSkeleton(await obj.asForm()),
        doneStep: SYNC_STEPS.SENDING,
      }
    );

  const discardKept = async (uuid) => {
    const local = await dexieTable.get({ uuid });
    const data = local?.isCreatedOffline
      ? null
      : await client.retrieve(uuid, SAVE_REQUEST).catch((error) => {
          if (error?.response?.status !== 404) throw error;
          return null;
        });
    await unmarkRecords([recordKey(model.tableName, uuid)]);
    if (data) await model.createOrUpdate(clearFlags(data));
    else await dexieTable.delete(uuid);
  };

  return {
    createSkeletonsOnBack,
    discardKept,
    innerCreate,
    innerBulkCreate,
    innerUpdate,
    innerBulkUpdate,
    innerFetch,
    innerDelete,
    innerBulkDelete,
    resyncToBack,
    innerPartialUpdate,
  };
};

export default innerFuncs;
